import { Tooltip as TooltipPrimitive } from "radix-ui";
import { type ReactElement, type ReactNode, useRef, useState } from "react";
import { cn } from "../lib/cn.js";

export interface TooltipProps {
  /** Nothing (undefined, empty) shows the trigger without a tooltip. */
  content: ReactNode;
  /** One focusable or hoverable element; it keeps its own accessible name. */
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}

/**
 * Tooltip (DESIGN.md): a small popover on hover and keyboard focus, in place of the browser's
 * `title`. Extra detail only, never the only label. Inside an open <dialog> it renders into
 * the dialog (the top layer would hide a tooltip on <body>).
 */
export function Tooltip({ content, children, side = "top", className }: TooltipProps) {
  // Typed as a button by Radix; asChild passes it to whatever element `children` is.
  const trigger = useRef<HTMLButtonElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  if (content === undefined || content === null || content === "") return children;
  return (
    <TooltipPrimitive.Provider delayDuration={400} skipDelayDuration={200}>
      <TooltipPrimitive.Root
        onOpenChange={(open) => {
          if (open) setContainer(trigger.current?.closest("dialog") ?? null);
        }}
      >
        <TooltipPrimitive.Trigger asChild ref={trigger}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container ?? undefined}>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              "z-50 max-w-72 border border-input bg-popover px-2.5 py-1.5 text-xs leading-snug whitespace-pre-line text-popover-foreground shadow-window animate-in fade-in-0 duration-100",
              className,
            )}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
